import React from 'react'
import { useTranslation } from 'react-i18next'

import { InsertionFlowScreensProps } from '../../../../../@types/props-types'
import {
  ContactInfoScreenValidationResult,
  OtherFacilitiesScreenValidationResult,
} from '../../../../../@types/validation-types'
import { FieldProps, SalutationString, Styles } from '../../../../../@types/types'
import { TextInput } from '../../../../common/inputs/textInput/TextInput'
import ChipOptionField from '../../../../common/inputs/chipOptionField/ChipOptionField'
import PhoneNumberWithToggleSwitch from './phoneNumberWithToggleSwitch/PhoneNumberWithToggleSwitch'
import { isWebView } from '../../../../../utils/navigation'
import { IconS24InfoCircle24Filled } from '@is24/cosma-ui-icons'

const style: Styles = {
  textInput: { textOverflow: 'ellipsis' },
}

export type ContactInfoFieldsProps = FieldProps<OtherFacilitiesScreenValidationResult> & {
  isReadOnlyMode?: boolean
}

const ContactInfoFields: React.FC<
  InsertionFlowScreensProps<ContactInfoScreenValidationResult> & ContactInfoFieldsProps
> = ({ screenName, insertionData, validationMessages, onChange, isReadOnlyMode }) => {
  const { t } = useTranslation()

  const isMobileView = isWebView()

  return (
    <>
      {!isMobileView && (
        <div className='grid-item one-whole' data-testid='edit-mode-contact-info-container'>
          <ChipOptionField
            id='salutation'
            propertyName='salutation'
            option={insertionData.salutation}
            options={SalutationString}
            translation='wizard.screen.contactInfo.salutation.'
            labelTranslationKey='wizard.screen.contactInfo.labels.salutation'
            errors={validationMessages!.salutation}
            enableDeselection={true}
            onChange={(value) => onChange({ screen: screenName, property: 'salutation' }, value)}
          />
        </div>
      )}
      <div className='grid-item palm-one-whole lap-one-whole desk-six-twelfth palm-margin-top-xl margin-top-l'>
        <TextInput
          disabled={isReadOnlyMode}
          id='first-name'
          propertyName='firstName'
          className='desk-margin-right-m'
          label={<>{t('wizard.screen.contactInfo.labels.firstName', 'First Name')}</>}
          value={insertionData.firstName}
          type='text'
          maxLength={30}
          errors={validationMessages!.firstName ?? []}
          doOnFieldChange={(property, value) => onChange({ screen: screenName, property }, value)}
          dataHeyimmoContext='contact_first_name'
        />
      </div>
      <div className='grid-item palm-one-whole lap-one-whole desk-six-twelfth palm-margin-top-xl margin-top-l desk-padding-left-m'>
        <TextInput
          disabled={isReadOnlyMode}
          id='last-name'
          propertyName='lastName'
          label={<>{t('wizard.screen.contactInfo.labels.lastName', 'Last Name*')}</>}
          value={insertionData.lastName}
          type='text'
          maxLength={50}
          style={style.textInput}
          errors={validationMessages!.lastName ?? []}
          doOnFieldChange={(property, value) => onChange({ screen: screenName, property }, value)}
          dataHeyimmoContext='contact_last_name'
        />
      </div>

      <div className='grid-item one-whole palm-margin-top-xl margin-top-l'>
        <TextInput
          disabled={isReadOnlyMode}
          id='email'
          propertyName='email'
          label={<>{t('wizard.screen.contactInfo.labels.email', 'E-Mail')}</>}
          value={insertionData.email}
          style={style.textInput}
          errors={validationMessages!.email ?? []}
          maxLength={150}
          doOnFieldChange={(property, value) => onChange({ screen: screenName, property }, value)}
          dataHeyimmoContext='contact_email'
        />
        {isMobileView && (
          <div data-testid='email-tip' className='grid-item one-whole grid-flex margin-top-s'>
            <IconS24InfoCircle24Filled style={{ color: 'var(--color-link-blue)' }} width={16} height={16} />
            <span className='font-body-small-regular margin-left-s' style={{ color: 'var(--color-link-blue)' }}>
              {t('wizard.screen.contactInfo.labels.email.tip')}
            </span>
          </div>
        )}
      </div>
      <PhoneNumberWithToggleSwitch
        disabled={isReadOnlyMode}
        phoneNumber={insertionData.phoneNumber}
        phoneNumberValidation={validationMessages!.phoneNumber ?? []}
        onChange={(property, value) => onChange({ screen: screenName, property }, value)}
      />
    </>
  )
}

export default ContactInfoFields
